import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/redwolfSdkAPI';
import { addClasses } from '../../utils/eventListeners';
import { passwordStrengthValidation } from '../../utils/validations';
import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import useHeadingFocuser from '../common/hooks/useHeadingFocuser';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';

import Spinner from '../common/components/Spinner';
import PublicContainer from '../containers/PublicContainer';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';
import { getToken } from '../../utils/localStorage';
import * as Sentry from '@sentry/react';
import { clearLocalStorage } from '../../utils/localStorage';

const UpdatePassword = () => {
	const [updatePasswordForm, setUpdatePasswordForm] = useState({
		currentPassword: '',
		newPassword: ''
	});
	const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(false);
	const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] = useState(false);
	const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [headingRef] = useHeadingFocuser();

	const [errorMsgs, setErrorMsgs] = useState({
		newPasswordErrorMessage: '',
		currentPasswordErrorMessage: ''
	});
	const [updatePasswordFormValid, setUpdatePasswordFormValid] = useState({
		isCurrentPasswordValid: true,
		isNewPasswordValid: true
	});
	const [passwordStrengthUpdateState, setPasswordStrengthUpdateState] = useState({
		isPasswordLengthPassed: false,
		isLowercasePassed: false,
		isUppercasePassed: false,
		isNumerPassed: false,
		isAllCasesPassed: false
	});
	const navigate = useNavigate();
	const currentPasswordRef = useRef<HTMLInputElement>(null);
	const newPasswordRef = useRef<HTMLInputElement>(null);
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Update password' });

	useEffect(() => {
		addClasses();

		let mainContainer: any = null;

		setTimeout(() => {
			mainContainer = document.getElementById('main-container');

			if (mainContainer) {
				mainContainer.classList.add('accout-login');
			}
		}, 0);
	}, []);

	const handleSubmit = (event: any) => {
		event.preventDefault();
		if (updatePasswordForm.currentPassword === updatePasswordForm.newPassword) {
			setErrorMsgs({
				...errorMsgs,
				newPasswordErrorMessage: 'Current Password & New Password must be different'
			});
			setUpdatePasswordFormValid({
				...updatePasswordFormValid,
				isNewPasswordValid: false,
				isCurrentPasswordValid: false
			});
			if (newPasswordRef.current !== null) {
				newPasswordRef.current.focus();
			}
		} else if (updatePasswordForm.currentPassword === '') {
			setErrorMsgs({
				...errorMsgs,
				currentPasswordErrorMessage: 'This is a required field.'
			});
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isCurrentPasswordValid: false });
			if (currentPasswordRef.current !== null) {
				currentPasswordRef.current.focus();
			}
		} else {
			setIsSpinnerLoading(true);
			const payAuthUpdatePasswordInfo = {
				oldPassword: updatePasswordForm.currentPassword,
				newPassword: updatePasswordForm.newPassword
			};

			logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CHANGE_PASSWORD');

			requestRedwolfService(authAPI.updatePassword(token, payAuthUpdatePasswordInfo))
				.then((response: any) => {
					setIsSpinnerLoading(false);
					if (response.constructor.$metadata$.simpleName === 'Success') {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_PASSWORD_SUCCESS');
						clearLocalStorage('isPasswordStrong');
						navigate('/en/account');
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_PASSWORD_FAILURE', {
							error: response.response.detail
						});

						if (response.response.detail === 'Password recently used. Use a different one.') {
							setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: response.response.detail });
							setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: false });
							if (newPasswordRef.current !== null) {
								newPasswordRef.current.focus();
							}
						} else if (response.response.detail === 'Password is incorrect.') {
							setErrorMsgs({ ...errorMsgs, currentPasswordErrorMessage: response.response.detail });
							setUpdatePasswordFormValid({
								...updatePasswordFormValid,
								isCurrentPasswordValid: false
							});
							if (currentPasswordRef.current !== null) {
								currentPasswordRef.current.focus();
							}
						} else {
							showTechnicalErrorMessage();
						}
					}
					transaction && transaction.end();
				})
				.catch(() => {
					transaction && transaction.end();
					showTechnicalErrorMessage();
					setIsSpinnerLoading(false);
				});
		}
	};

	const validatePasswordOnBlur = (event: any) => {
		if (updatePasswordForm.currentPassword === '' && event.target.name === 'currentPassword') {
			setErrorMsgs({ ...errorMsgs, currentPasswordErrorMessage: 'This is a required field.' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isCurrentPasswordValid: false });
		} else if (updatePasswordForm.newPassword === '' && event.target.name === 'newPassword') {
			setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: 'This is a required field.' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: false });
		} else if (
			!passwordStrengthUpdateState.isAllCasesPassed &&
			updatePasswordForm.newPassword !== ''
		) {
			setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: 'Password requirements not met.' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: false });
		}
	};

	const passwordStrengthHandler = (event: any) => {
		const { name, value } = event.target;
		setUpdatePasswordForm({ ...updatePasswordForm, [name]: value });

		if (name === 'newPassword') {
			const passwordStrengthUpdateState = passwordStrengthValidation(value);
			setPasswordStrengthUpdateState(passwordStrengthUpdateState);
			if (value) {
				setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: '' });
				setUpdatePasswordFormValid({ ...updatePasswordFormValid, isNewPasswordValid: true });
			}
		} else if (name === 'currentPassword' && value) {
			setErrorMsgs({ ...errorMsgs, currentPasswordErrorMessage: '' });
			setUpdatePasswordFormValid({ ...updatePasswordFormValid, isCurrentPasswordValid: true });
		}

		if (
			(name === 'currentPassword' || name === 'newPassword') &&
			value &&
			errorMsgs.newPasswordErrorMessage === 'Current Password & New Password must be different'
		) {
			setErrorMsgs({ ...errorMsgs, newPasswordErrorMessage: '' });
			setUpdatePasswordFormValid({ isCurrentPasswordValid: true, isNewPasswordValid: true });
		}
	};

	const passwordShowHideToggle = (event: any) => {
		if (event.key === 'Enter') {
			if (event.target.id === 'new-password') {
				setIsNewPasswordVisible(!isNewPasswordVisible);
			} else {
				setIsCurrentPasswordVisible(!isCurrentPasswordVisible);
			}
		}
	};

	useEffect(() => {
		enableContinueButton();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatePasswordFormValid]);

	const enableContinueButton = () => {
		if (
			updatePasswordFormValid.isCurrentPasswordValid &&
			updatePasswordFormValid.isNewPasswordValid &&
			passwordStrengthUpdateState.isAllCasesPassed
		) {
			setIsContinueButtonDisabled(true);
		} else {
			setIsContinueButtonDisabled(false);
		}
	};

	return (
		<PublicContainer>
			<div className="passwordupdate">
				<div className="pw-update">
					<h1 className="h1" ref={headingRef} tabIndex={-1} aria-live="polite">
						Update your password
					</h1>
					<p className="desc">To improve security, your password needs to be updated.</p>
					<div className="psw-info-component">
						<div className="psw-info">
							<p className="psw-title">Please Include:</p>
							<ul className="checklist-info">
								<li
									className={
										passwordStrengthUpdateState.isPasswordLengthPassed ? 'item' : 'item incomplete'
									}
									aria-label="8 to 25 characters">
									<span aria-hidden="true">8-25 characters</span>
								</li>
								<li
									className={
										passwordStrengthUpdateState.isLowercasePassed ? 'item' : 'item incomplete'
									}
									aria-label="1 lowercase letter">
									<span aria-hidden="true">1 lowercase letter</span>
								</li>
								<li
									className={
										passwordStrengthUpdateState.isUppercasePassed ? 'item' : 'item incomplete'
									}
									aria-label="1 uppercase letter">
									<span aria-hidden="true">1 uppercase letter</span>
								</li>
								<li
									className={passwordStrengthUpdateState.isNumerPassed ? 'item' : 'item incomplete'}
									aria-label="1 number">
									<span aria-hidden="true">1 number</span>
								</li>
							</ul>
						</div>
					</div>
					<form className="update-pw-form">
						<ul className="form-list">
							<li className="fields">
								<div
									className={
										!updatePasswordFormValid.isCurrentPasswordValid ? 'field has-error' : 'field'
									}>
									<div
										className={
											isCurrentPasswordVisible
												? 'input-box form-group input-label pw-view'
												: 'input-box form-group input-label'
										}>
										<input
											name="currentPassword"
											type={isCurrentPasswordVisible ? 'text' : 'password'}
											className="form-control password"
											id="currentPassword"
											autoComplete="current-password"
											aria-required="true"
											aria-invalid="false"
											aria-describedby="currentPasswordError"
											onChange={passwordStrengthHandler}
											onBlur={validatePasswordOnBlur}
											value={updatePasswordForm.currentPassword}
											ref={currentPasswordRef}
										/>
										<label htmlFor="currentPassword" className="control-label">
											Current Password
										</label>
										<code id="currentPasswordError">{errorMsgs.currentPasswordErrorMessage}</code>
										<div
											className="input-field-icon"
											tabIndex={0}
											role="button"
											aria-label={isCurrentPasswordVisible ? 'hide password' : 'show password'}
											id="old-password"
											onClick={() => setIsCurrentPasswordVisible(!isCurrentPasswordVisible)}
											onKeyDown={passwordShowHideToggle}>
											<em aria-hidden="true" className="icon-eye eye-icon"></em>
										</div>
									</div>
								</div>
							</li>
							<li className="fields">
								<div
									className={
										!updatePasswordFormValid.isNewPasswordValid ? 'field has-error' : 'field'
									}>
									<div
										className={
											isNewPasswordVisible
												? 'input-box form-group input-label pw-view'
												: 'input-box form-group input-label'
										}>
										<input
											name="newPassword"
											type={isNewPasswordVisible ? 'text' : 'password'}
											className="form-control password"
											id="newPassword"
											autoComplete="new-password"
											aria-required="true"
											aria-invalid="false"
											aria-describedby="newPasswordError"
											onChange={passwordStrengthHandler}
											onBlur={validatePasswordOnBlur}
											value={updatePasswordForm.newPassword}
											ref={newPasswordRef}
										/>
										<label htmlFor="newPassword" className="control-label">
											New Password
										</label>
										<code id="newPasswordError">{errorMsgs.newPasswordErrorMessage}</code>
										<div
											className="input-field-icon"
											tabIndex={0}
											role="button"
											aria-label={isNewPasswordVisible ? 'hide password' : 'show password'}
											id="new-password"
											onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}
											onKeyDown={passwordShowHideToggle}>
											<em aria-hidden="true" className="icon-eye eye-icon"></em>
										</div>
									</div>
								</div>
							</li>
						</ul>
						<button
							id="update_button"
							type="submit"
							className="primary-button"
							title="Continue"
							onClick={handleSubmit}
							disabled={!isContinueButtonDisabled}>
							Continue
						</button>
					</form>
					{isSpinnerLoading && <Spinner />}
				</div>
			</div>
		</PublicContainer>
	);
};

export default UpdatePassword;
